import { useEffect, useState } from 'react'
import { useDebouncedValue } from 'hooks/useDebouncedValue'
import { log } from 'utils/logger'

const isAbortLikeError = (error: unknown, signal: AbortSignal): boolean =>
    signal.aborted || (typeof error === 'object' && error !== null && 'name' in error && (error.name === 'CanceledError' || error.name === 'AbortError'))

type UseDebouncedAsyncSearchParams<T> = {
    enabled: boolean
    search: string
    emptyData: () => T
    fetcher: (params: { search: string; signal: AbortSignal }) => Promise<T>
    logMessage: string
    getErrorMessage: (error: unknown) => string
}

export const useDebouncedAsyncSearch = <T>({
    enabled,
    search,
    emptyData,
    fetcher,
    logMessage,
    getErrorMessage,
}: UseDebouncedAsyncSearchParams<T>): { data: T; loading: boolean; error: string | null } => {
    const debouncedSearch = useDebouncedValue(search.trim(), 250)
    const [data, setData] = useState<T>(() => emptyData())
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!enabled) {
            setData(emptyData())
            setLoading(false)
            setError(null)
            return
        }

        const controller = new AbortController()
        const runSearch = async () => {
            setLoading(true)
            setError(null)
            try {
                const nextData = await fetcher({ search: debouncedSearch, signal: controller.signal })
                if (controller.signal.aborted) return
                setData(nextData)
                setError(null)
            } catch (error) {
                if (!isAbortLikeError(error, controller.signal)) {
                    log.e(logMessage, error)
                    setData(emptyData())
                    setError(getErrorMessage(error))
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false)
            }
        }

        void runSearch()

        return () => {
            controller.abort()
        }
    }, [debouncedSearch, emptyData, enabled, fetcher, getErrorMessage, logMessage])

    return { data, loading, error }
}
